import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import NightlightIcon from "@mui/icons-material/Nightlight";
import WbSunnyIcon from "@mui/icons-material/WbSunny";
import { Box, Typography } from "@mui/material";
import { type FC, memo } from "react";
import { useHospitalData } from "../../../contexts/HospitalDataProvider";
import { INK_BLACK, NEUTRAL, SHIFT_NIGHT_FALLBACK, STATUS_BADGE } from "../../../theme/tokens";
import type { DayItem } from "../../../utils/models";
import { CountBadge } from "./CountBadge";
import { DAY_WIDTH } from "./consts";
import { RequiredBreakdownInfo } from "./RequiredBreakdownPopover";
import type { RoleSection, TimeTitle } from "./useWeeklyGapView";

export const TimeTitleRow: FC<{
	title: TimeTitle;
	isExpanded: boolean;
	onToggle: () => void;
	weekViewData: DayItem[];
	section: RoleSection;
}> = memo(({ title, isExpanded, onToggle, weekViewData, section }) => {
	// Only hospital demos (Hospital type + inhouse2).
	const { isHospitalView: isHospitalDemoView } = useHospitalData();
	return (
		<Box
			data-testid={`weekly-time-title-row-${title.label}`}
			sx={{
				bgcolor: "white",
				display: "flex",
				alignItems: "center",
				cursor: "pointer",
				"&:hover": { bgcolor: NEUTRAL[50] },
				transition: "background-color 0.15s ease",
				borderBottom: `1px solid ${NEUTRAL[300]}`,
			}}
			onClick={onToggle}
		>
			{weekViewData.map((dayItem, dayIdx) => {
				const { actual, required } = title.dayTotals[dayIdx];

				return (
					<Box
						key={`TimeTitleRow-${dayItem.date.toISO()}`}
						data-testid={`weekly-time-title-day-${dayIdx}`}
						sx={{
							width: DAY_WIDTH,
							minWidth: DAY_WIDTH,
							display: "flex",
							alignItems: "baseline",
							px: 1,
							py: 0.5,
							borderLeft: `1px solid ${NEUTRAL[300]}`,
							transition: "border-color 0.12s ease",
							flexWrap: "wrap",
							gap: 0.75,
							"& .required-breakdown-icon": { opacity: 0, transition: "opacity 0.15s ease" },
							"&:hover .required-breakdown-icon": { opacity: 1 },
						}}
					>
						{dayIdx === 0 && (
							<Box
								sx={{
									display: "flex",
									alignItems: "baseline",
									gap: 0.5,
								}}
							>
								<Box
									sx={{
										display: "flex",
										alignItems: "baseline",
									}}
								>
									<KeyboardArrowDownIcon
										sx={{
											fontSize: 14,
											color: "primary.main",
											transform: isExpanded ? "rotate(180deg)" : "rotate(0deg)",
											transition: "transform 0.2s ease",
										}}
									/>
									{title.startMin / 60 >= 5 && title.startMin / 60 < 17 ? (
										// Demo: black day/night icons; others keep the colored ones.
										<WbSunnyIcon sx={{ fontSize: 14, color: isHospitalDemoView ? INK_BLACK : STATUS_BADGE.ORANGE, flexShrink: 0 }} />
									) : (
										<NightlightIcon sx={{ fontSize: 14, color: isHospitalDemoView ? INK_BLACK : SHIFT_NIGHT_FALLBACK, flexShrink: 0 }} />
									)}
								</Box>
								<Typography sx={{ fontSize: "16px", color: "primary.main", whiteSpace: "nowrap" }}>{title.label}</Typography>
							</Box>
						)}
						<Typography sx={{ fontSize: "12px", color: "primary.main", whiteSpace: "nowrap" }}>{dayItem.date.toFormat("ccc")}</Typography>
						<CountBadge actual={actual} required={required} />
						<RequiredBreakdownInfo dayItem={dayItem} title={title} dayIdx={dayIdx} groupLabel={section.shortRole} />
					</Box>
				);
			})}
		</Box>
	);
});
